import type { SkillBranch } from '@sorrem/shared';
import { SKILL_TREE_BRANCH_RULES } from './skill-tree-model';
import { ESSENCE_RING_CENTERS } from './skill-tree-pieces';

/**
 * How one part of a node enters during the Skills tab intro:
 * - `pop`: scales up from small with a slight overshoot.
 * - `materialize`: condenses from slightly larger.
 * - `settle`: drops into place from larger, without overshoot.
 * - `grow-out`: travels out from the node's center to its place as it grows.
 * - `fade`: fades in where it is.
 */
export type SkillTreeIntroEffect = 'pop' | 'materialize' | 'settle' | 'grow-out' | 'fade';

/** One part's entrance, in seconds from the start of its node's entrance. */
export interface SkillTreeIntroStep {
    readonly effect: SkillTreeIntroEffect;
    readonly start: number;
    readonly duration: number;
}

/**
 * The parts a node is drawn from. `marker` is a Martial ring segment or an
 * Essence level ring (index from the first); `path` is the stretch of
 * Essence spiral leading into a ring; `mastered` is the piece a mastered
 * node shows instead of its markers.
 */
export type SkillTreeNodePart =
    | { readonly kind: 'core' }
    | { readonly kind: 'glow' }
    | { readonly kind: 'icon' }
    | { readonly kind: 'label' }
    | { readonly kind: 'marker'; readonly index: number }
    | { readonly kind: 'path'; readonly index: number }
    | { readonly kind: 'mastered' };

/** A node type's entrance: when and how each of its parts appears, once the link from its dial arrives. */
export interface SkillTreeNodeIntro {
    /** Seconds from the start of the entrance until every part has arrived. */
    readonly duration: number;
    /** The part's entrance, or null for a part that is simply there from the start. */
    step(part: SkillTreeNodePart): SkillTreeIntroStep | null;
}

/** Martial: the core pops in, then the ring's segments appear one by one, clockwise from the top. */
const MARTIAL_SEGMENTS = SKILL_TREE_BRANCH_RULES.martial.maxStep;
const MARTIAL_SEGMENT_START = 0.25;
const MARTIAL_SEGMENT_STAGGER = 0.045;
const MARTIAL_SEGMENT_DURATION = 0.18;

export const MARTIAL_NODE_INTRO: SkillTreeNodeIntro = {
    duration: MARTIAL_SEGMENT_START + (MARTIAL_SEGMENTS - 1) * MARTIAL_SEGMENT_STAGGER + MARTIAL_SEGMENT_DURATION,
    step(part) {
        switch (part.kind) {
            case 'core':
                return { effect: 'pop', start: 0, duration: 0.3 };
            case 'glow':
                return { effect: 'fade', start: 0.1, duration: 0.3 };
            case 'icon':
                return { effect: 'fade', start: 0.15, duration: 0.25 };
            case 'label':
                return { effect: 'fade', start: 0.2, duration: 0.3 };
            case 'marker':
                return { effect: 'pop', start: MARTIAL_SEGMENT_START + part.index * MARTIAL_SEGMENT_STAGGER, duration: MARTIAL_SEGMENT_DURATION };
            case 'mastered':
                // The fused ring arrives whole, after the core.
                return { effect: 'pop', start: MARTIAL_SEGMENT_START, duration: 0.4 };
            case 'path':
                return null;
        }
    },
};

/** Essence: the core ring materializes first, then each level ring grows out of it to its place on the spiral, one after another. */
const ESSENCE_RING_START = 0.3;
const ESSENCE_RING_STAGGER = 0.1;
const ESSENCE_RING_DURATION = 0.35;

export const ESSENCE_NODE_INTRO: SkillTreeNodeIntro = {
    duration: ESSENCE_RING_START + (ESSENCE_RING_CENTERS.length - 1) * ESSENCE_RING_STAGGER + ESSENCE_RING_DURATION,
    step(part) {
        switch (part.kind) {
            case 'core':
                return { effect: 'materialize', start: 0, duration: 0.35 };
            case 'icon':
                return { effect: 'fade', start: 0.15, duration: 0.25 };
            case 'label':
                return { effect: 'fade', start: 0.2, duration: 0.3 };
            case 'marker':
                return { effect: 'grow-out', start: ESSENCE_RING_START + part.index * ESSENCE_RING_STAGGER, duration: ESSENCE_RING_DURATION };
            case 'path':
                // The spiral into a ring appears as that ring travels out along it.
                return { effect: 'fade', start: ESSENCE_RING_START + part.index * ESSENCE_RING_STAGGER + 0.05, duration: ESSENCE_RING_DURATION - 0.05 };
            case 'mastered':
                // The blazing spiral, behind the rings (which grow out as markers).
                return { effect: 'fade', start: ESSENCE_RING_START, duration: 0.5 };
            case 'glow':
                return null;
        }
    },
};

/** Crafting (unawakened in alpha): the cross settles down into place, then its icon and name fade in. */
export const CRAFTING_NODE_INTRO: SkillTreeNodeIntro = {
    duration: 0.45,
    step(part) {
        switch (part.kind) {
            case 'core':
                return { effect: 'settle', start: 0, duration: 0.45 };
            case 'icon':
                return { effect: 'fade', start: 0.15, duration: 0.25 };
            case 'label':
                return { effect: 'fade', start: 0.15, duration: 0.3 };
            case 'glow':
            case 'marker':
            case 'path':
            case 'mastered':
                return null;
        }
    },
};

/** Each node type's entrance. */
export const SKILL_TREE_NODE_INTROS: Record<SkillBranch, SkillTreeNodeIntro> = {
    martial: MARTIAL_NODE_INTRO,
    essence: ESSENCE_NODE_INTRO,
    crafting: CRAFTING_NODE_INTRO,
};
